import { faFileCsv, faFileExcel } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import CardMembershipIcon from '@mui/icons-material/CardMembership';
import DateRangeIcon from '@mui/icons-material/DateRange';
import MessageIcon from '@mui/icons-material/Message';
import MovieIcon from '@mui/icons-material/Movie';
import PostAddIcon from '@mui/icons-material/PostAdd';
import TollIcon from '@mui/icons-material/Toll';
import { Box, Button, Grid, IconButton, LinearProgress, Skeleton, Stack, Tooltip, Typography, useTheme } from '@mui/material';
import dinero from 'dinero.js';
import { Parser } from 'json2csv';
import moment from 'moment-timezone';
import React, { useState } from 'react';
import * as XLSX from 'xlsx';
import useSextforceMetricsAccountSubscriptionsAndEarningsGroupByMonth from '../../../../../../hooks/useSextforceMetricsAccountSubscriptionsAndEarningsGroupByMonth';
import DateRangeDialog from './DateRangeDialog';
import MonthlyPerformanceCard from './MonthlyPerformanceCard';
import SubscriberCounter from './SubscriberCounter';

type SubscriptionsAndEarningsCardProps = {
    timezone: string;
};

const SubscriptionsAndEarningsCard = (props: SubscriptionsAndEarningsCardProps) => {
    const { timezone } = props;
    const theme = useTheme();

    // Set default date range to the last 3 months
    const [startDate, setStartDate] = useState<moment.Moment>(moment().startOf('month').startOf('day'));
    const [endDate, setEndDate] = useState<moment.Moment>(moment().endOf('day'));

    const { data, isLoading } = useSextforceMetricsAccountSubscriptionsAndEarningsGroupByMonth(
        startDate.toDate(),
        endDate.toDate(),
        timezone,
    );

    // Dialog state
    const [dateRangeDialogOpen, setDateRangeDialogOpen] = useState(false);
    const [tempStartDate, setTempStartDate] = useState<moment.Moment | null>(startDate);
    const [tempEndDate, setTempEndDate] = useState<moment.Moment | null>(endDate);

    // Function to format currency using dinero.js
    const formatCurrency = (amount: number) => {
        // Convert amount to cents for dinero.js (which works with integer values)
        const amountInCents = Math.round(amount * 100);
        const money = dinero({ amount: amountInCents, currency: 'USD' });

        // Format with dinero.js
        return money.toFormat();
    };

    // Calculate totals if data exists
    const getTotals = () => {
        if (!data) return null;

        return {
            totalEarnings: data.reduce((sum, item) => sum + item.amountNet, 0),
            totalSubscribers: data.reduce((sum, item) => sum + item.subsCount, 0),
            totalSpenders: data.reduce((sum, item) => sum + item.spendersCount, 0),
        };
    };

    const totals = getTotals();

    // Function to get the most recent month data
    const getMostRecentMonth = () => {
        if (!data || data.length === 0) return null;
        return data[data.length - 1];
    };

    const latestMonth = getMostRecentMonth();

    // Sort data by date for chronological display
    const sortedData = React.useMemo(() => {
        if (!data) return [];
        return [...data].sort((a, b) => {
            const dateA = moment(`${a.year}-${a.month}-01`);
            const dateB = moment(`${b.year}-${b.month}-01`);
            return dateA.diff(dateB);
        });
    }, [data]);

    // Handle dialog open
    const handleOpenDateRangeDialog = () => {
        setTempStartDate(startDate);
        setTempEndDate(endDate);
        setDateRangeDialogOpen(true);
    };

    // Handle dialog close
    const handleCloseDateRangeDialog = () => {
        setDateRangeDialogOpen(false);
    };

    // Handle apply date range
    const handleApplyDateRange = () => {
        if (tempStartDate && tempEndDate) {
            // Set start date to beginning of day
            const newStartDate = moment(tempStartDate).startOf('day');
            // Set end date to end of day
            const newEndDate = moment(tempEndDate).endOf('day');

            setStartDate(newStartDate);
            setEndDate(newEndDate);
        }
        setDateRangeDialogOpen(false);
    };

    // Handle predefined date ranges
    const handlePredefinedRange = (range: string) => {
        let newStartDate: moment.Moment;
        let newEndDate: moment.Moment = moment().endOf('day');

        switch (range) {
            case 'thisMonth':
                newStartDate = moment().startOf('month');
                newEndDate = moment().endOf('day');
                break;
            case 'lastMonth':
                newStartDate = moment().subtract(1, 'month').startOf('month');
                newEndDate = moment().subtract(1, 'month').endOf('month');
                break;
            case 'last3Months':
                newStartDate = moment().subtract(3, 'months').startOf('month');
                newEndDate = moment().endOf('day');
                break;
            case 'thisQuarter':
                newStartDate = moment().startOf('quarter');
                newEndDate = moment().endOf('day');
                break;
            case 'lastQuarter':
                newStartDate = moment().subtract(1, 'quarter').startOf('quarter');
                newEndDate = moment().subtract(1, 'quarter').endOf('quarter');
                break;
            case 'thisYear':
                newStartDate = moment().startOf('year');
                newEndDate = moment().endOf('day');
                break;
            case 'lastYear':
                newStartDate = moment().subtract(1, 'year').startOf('year');
                newEndDate = moment().subtract(1, 'year').endOf('year');
                break;
            default:
                newStartDate = moment().subtract(3, 'months').startOf('month');
                newEndDate = moment().endOf('day');
        }

        setStartDate(newStartDate);
        setEndDate(newEndDate);
        setDateRangeDialogOpen(false);
    };
    // Generate revenue sources data for the most recent month
    const getRevenueSources = () => {
        if (!latestMonth) return [];

        return [
            {
                name: 'Subscriptions',
                amount: latestMonth.subscriptionsAmountNet,
                count: latestMonth.subscriptionsCount,
                icon: <CardMembershipIcon />,
            },
            {
                name: 'Messages/PPVs',
                amount: latestMonth.messagesAmountNet,
                count: latestMonth.messagesCount,
                icon: <MessageIcon />,
            },
            {
                name: 'Tips',
                amount: latestMonth.tipsAmountNet,
                count: latestMonth.tipsCount,
                icon: <TollIcon />,
            },
            {
                name: 'Posts',
                amount: latestMonth.postsAmountNet,
                count: latestMonth.postsCount,
                icon: <PostAddIcon />,
            },
            {
                name: 'Streams',
                amount: latestMonth.streamsAmountNet,
                count: latestMonth.streamsCount,
                icon: <MovieIcon />,
            },
        ];
    };

    // Get revenue breakdown percentages
    const getRevenueBreakdown = () => {
        if (!latestMonth) return [];

        const total = latestMonth.amountNet;
        if (total === 0) return [];

        const sources = getRevenueSources();
        return sources.map(source => ({
            ...source,
            percentage: (source.amount / total) * 100,
        }));
    };

    const revenueBreakdown = getRevenueBreakdown();

    // Export to CSV
    const exportToCSV = () => {
        if (!data || data.length === 0) return;

        try {
            // Format data for export
            const exportData = data.map(item => ({
                Year: item.year,
                Month: item.month,
                MonthYear: moment(`${item.year}-${item.month}-01`).format('MMMM YYYY'),
                Subscribers: item.subsCount,
                Spenders: item.spendersCount,
                TotalRevenue: item.amountNet.toFixed(2),
                SubscriptionsRevenue: item.subscriptionsAmountNet.toFixed(2),
                MessagesRevenue: item.messagesAmountNet.toFixed(2),
                TipsRevenue: item.tipsAmountNet.toFixed(2),
                PostsRevenue: item.postsAmountNet.toFixed(2),
                StreamsRevenue: item.streamsAmountNet.toFixed(2),
                SubscriptionsCount: item.subscriptionsCount,
                MessagesCount: item.messagesCount,
                TipsCount: item.tipsCount,
                PostsCount: item.postsCount,
                StreamsCount: item.streamsCount,
            }));

            // Define fields for CSV
            const fields = [
                'MonthYear',
                'Year',
                'Month',
                'Subscribers',
                'Spenders',
                'TotalRevenue',
                'SubscriptionsRevenue',
                'MessagesRevenue',
                'TipsRevenue',
                'PostsRevenue',
                'StreamsRevenue',
                'SubscriptionsCount',
                'MessagesCount',
                'TipsCount',
                'PostsCount',
                'StreamsCount',
            ];

            // Create parser
            const json2csvParser = new Parser({ fields });
            const csv = json2csvParser.parse(exportData);

            // Create download link
            const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.setAttribute('href', url);
            link.setAttribute('download', `subscriber_earnings_${moment().format('YYYY-MM-DD')}.csv`);
            link.style.visibility = 'hidden';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } catch (error) {
            console.error('Error exporting to CSV:', error);
        }
    };

    // Export to Excel
    const exportToExcel = () => {
        if (!data || data.length === 0) return;

        try {
            // Format data for export
            const exportData = data.map(item => ({
                'Month/Year': moment(`${item.year}-${item.month}-01`).format('MMMM YYYY'),
                Year: item.year,
                Month: item.month,
                Subscribers: item.subsCount,
                Spenders: item.spendersCount,
                'Total Revenue': item.amountNet.toFixed(2),
                'Subscriptions Revenue': item.subscriptionsAmountNet.toFixed(2),
                'Messages (PPVs) Revenue': item.messagesAmountNet.toFixed(2),
                'Tips Revenue': item.tipsAmountNet.toFixed(2),
                'Posts Revenue': item.postsAmountNet.toFixed(2),
                'Streams Revenue': item.streamsAmountNet.toFixed(2),
                'Subscriptions Count': item.subscriptionsCount,
                'Messages Count': item.messagesCount,
                'Tips Count': item.tipsCount,
                'Posts Count': item.postsCount,
                'Streams Count': item.streamsCount,
            }));

            // Create workbook and worksheet
            const worksheet = XLSX.utils.json_to_sheet(exportData);
            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, worksheet, 'Subscriber Earnings');

            // Set column widths
            const columnWidths = [
                { wch: 15 }, // Month/Year
                { wch: 6 }, // Year
                { wch: 6 }, // Month
                { wch: 12 }, // Subscribers
                { wch: 10 }, // Spenders
                { wch: 14 }, // Total Revenue
                { wch: 20 }, // Subscriptions Revenue
                { wch: 16 }, // Messages Revenue
                { wch: 14 }, // Tips Revenue
                { wch: 14 }, // Posts Revenue
                { wch: 16 }, // Streams Revenue
                { wch: 18 }, // Subscriptions Count
                { wch: 14 }, // Messages Count
                { wch: 12 }, // Tips Count
                { wch: 12 }, // Posts Count
                { wch: 14 }, // Streams Count
            ];
            worksheet['!cols'] = columnWidths;

            // Save file
            XLSX.writeFile(workbook, `subscriber_earnings_${moment().format('YYYY-MM-DD')}.xlsx`);
        } catch (error) {
            console.error('Error exporting to Excel:', error);
        }
    };

    return (
        <Box>
            {isLoading && (
                <Box sx={{ width: '100%' }}>
                    <Skeleton variant="rectangular" width="100%" height={40} sx={{ mb: 2 }} />
                    <Skeleton variant="rectangular" width="100%" height={120} sx={{ mb: 2 }} />
                    <Skeleton variant="rectangular" width="100%" height={200} />
                </Box>
            )}

            {!isLoading && !data && (
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        height: 200,
                    }}
                >
                    <Typography variant="h6" color="textSecondary">
                        No subscribers data available for the selected period
                    </Typography>
                </Box>
            )}

            {!isLoading && data && data.length > 0 && (
                <Box>
                    <Box sx={{ mb: 3 }}>
                        <Typography variant="h6" gutterBottom>
                            Subscriber Earnings Overview
                        </Typography>

                        <Stack direction="row" spacing={1} alignItems={'center'}>
                            <Typography variant="body2" color="textSecondary" gutterBottom>
                                {`${moment(startDate).format('MMM D, YYYY')} - ${moment(endDate).format('MMM D, YYYY')}`}
                            </Typography>
                            <Button variant="outlined" startIcon={<DateRangeIcon />} onClick={handleOpenDateRangeDialog} size="small">
                                Change
                            </Button>
                            <Tooltip title="Export to CSV">
                                <IconButton
                                    color="secondary"
                                    onClick={() => {
                                        exportToCSV();
                                    }}
                                    size="small"
                                    disabled={!data || data.length === 0}
                                >
                                    <FontAwesomeIcon
                                        icon={faFileCsv}
                                        size="2x"
                                        color={!data || data.length === 0 ? theme.palette.text.disabled : theme.palette.info.main}
                                    />
                                </IconButton>
                            </Tooltip>
                            <Tooltip title="Export to Excel">
                                <IconButton color="success" onClick={exportToExcel} size="small" disabled={!data || data.length === 0}>
                                    <FontAwesomeIcon
                                        icon={faFileExcel}
                                        size="2x"
                                        color={!data || data.length === 0 ? theme.palette.text.disabled : theme.palette.info.main}
                                    />
                                </IconButton>
                            </Tooltip>
                        </Stack>
                    </Box>

                    {/* Totals Summary */}
                    <Grid container spacing={2} sx={{ mb: 4 }}>
                        <Grid item xs={12} sm={4}>
                            <SubscriberCounter
                                count={totals?.totalEarnings || 0}
                                label="Total Net Earnings"
                                color={theme.palette.primary.main}
                                iconColor="white"
                                isCurrency={true}
                            />
                        </Grid>

                        <Grid item xs={12} sm={4}>
                            <SubscriberCounter
                                count={totals?.totalSubscribers || 0}
                                label="Total Subscribers"
                                color={theme.palette.secondary.main}
                            />
                        </Grid>

                        <Grid item xs={12} sm={4}>
                            <SubscriberCounter count={totals?.totalSpenders || 0} label="Total Spenders" color={theme.palette.info.main} />
                        </Grid>
                    </Grid>

                    {/* Latest Month Revenue Breakdown */}
                    {latestMonth && (
                        <Box sx={{ mb: 4 }}>
                            <Typography variant="h6" gutterBottom>
                                {moment(`${latestMonth.year}-${latestMonth.month}-01`).format('MMMM YYYY')} Revenue Breakdown
                            </Typography>

                            <Stack spacing={2}>
                                {revenueBreakdown.map((item, index) => (
                                    <Box key={index}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                {item.icon}
                                                <Typography variant="body2" sx={{ ml: 1 }}>
                                                    {item.name} ({item.count})
                                                </Typography>
                                            </Box>
                                            <Box>
                                                <Typography variant="body2" fontWeight="bold">
                                                    {formatCurrency(item.amount)}
                                                </Typography>
                                                <Typography variant="caption" color="textSecondary" align="right" display="block">
                                                    {item.percentage.toFixed(1)}%
                                                </Typography>
                                            </Box>
                                        </Box>
                                        <LinearProgress
                                            variant="determinate"
                                            value={item.percentage}
                                            sx={{
                                                height: 8,
                                                borderRadius: 1,
                                                mb: 1,
                                                backgroundColor: theme.palette.grey[200],
                                                '& .MuiLinearProgress-bar': {
                                                    backgroundColor:
                                                        index === 0
                                                            ? theme.palette.primary.main
                                                            : index === 1
                                                            ? theme.palette.secondary.main
                                                            : index === 2
                                                            ? theme.palette.error.main
                                                            : index === 3
                                                            ? theme.palette.warning.main
                                                            : theme.palette.info.main,
                                                },
                                            }}
                                        />
                                    </Box>
                                ))}
                            </Stack>
                        </Box>
                    )}

                    {/* Monthly Metrics */}
                    <Typography variant="h6" gutterBottom>
                        Monthly Performance
                    </Typography>
                    <Grid container spacing={2}>
                        {sortedData.map((monthData, index) => {
                            // Calculate max values for reference
                            const maxEarnings = Math.max(...sortedData.map(m => m.amountNet));
                            const maxSubs = Math.max(...sortedData.map(m => m.subsCount));
                            const maxSpenders = Math.max(...sortedData.map(m => m.spendersCount));

                            return (
                                <Grid item xs={12} sm={6} md={4} key={index}>
                                    <MonthlyPerformanceCard
                                        month={monthData}
                                        maxEarnings={maxEarnings}
                                        maxSubs={maxSubs}
                                        maxSpenders={maxSpenders}
                                    />
                                </Grid>
                            );
                        })}
                    </Grid>
                </Box>
            )}

            {/* Date Range Selection Dialog */}
            <DateRangeDialog
                open={dateRangeDialogOpen}
                onClose={handleCloseDateRangeDialog}
                tempStartDate={tempStartDate}
                tempEndDate={tempEndDate}
                setTempStartDate={setTempStartDate}
                setTempEndDate={setTempEndDate}
                onApply={handleApplyDateRange}
                onSelectPredefinedRange={handlePredefinedRange}
            />
        </Box>
    );
};

export default SubscriptionsAndEarningsCard;
